import { gql } from '@apollo/client';
import { blockFragments } from '../blocks';
import { crewMemberFragments } from '../crew-member';
import { workPackageFragments } from '../work-packages';

const mapMarkerFragments = {
  mapMarker: gql`
    fragment MapMarker on MapMarker {
      id
      displayIdentifier
      type
      geoLocation {
        latitude
        longitude
      }
      locationDescription
    }
  `,
};
const fragments = {
  attachment: gql`
    fragment Attachment on Attachment {
      id
      attachmentType
      createdAt
      creator {
        firstName
        lastName
        id
      }
      fileName
      fileSize
      mimeType
      url
    }
  `,
  weldItem: gql`
    fragment WeldItem on Weld {
      id
      milestones {
        id
        name
        currentStatus {
          id
          codeI18n
          rejectionType {
            id
            reason
          }
          comments
          createdAt
          createdBy {
            id
            firstName
            lastName
          }
        }
      }
      status {
        id
        status
        statusAt
        statusBy {
          ...CrewMember
        }
      }
      ${crewMemberFragments.crewMember}
    }
  `,

  fitupRequest: gql`
    fragment fitupRequest on FitupRequest {
      id
      isFitupRequired
      isWeldingRequired
      installationCrewMember {
        ...CrewMember
      }
      comments
      ${crewMemberFragments.crewMember}
    }
  `,
  weldItemWithRejection: gql`
  fragment WeldItemWithRejection on Weld {
    id
    milestones {
      id
      name
      type {
        id
      }
      isExpired
      currentStatus {
        id
        codeI18n
        rejectionType {
          id
          reason
        }
        rejectionSubReasonTypes {
          id
          reason
          comments
       }
        comments
        createdAt
        createdBy {
          id
          firstName
          lastName
        }
      }
    }
    status {
      id
      status
      statusAt
      statusBy {
        ...CrewMember
      }
    }
    ${crewMemberFragments.crewMember}
  }
`,
  weldItemWithAttachment: gql`
    fragment WeldItemWithAttachment on Weld {
      id
      weldedWithProcedure {
        id
        procedure
      }
      locationDescription
        location {
          latitude
          longitude
      }
      milestones {
        id
        name
        attachments {
          id
          url
          createdAt
          creator {
            ...CrewMemberStencil
          }
        }
        currentStatus {
          id
          codeI18n
          rejectionType {
            id
            reason
          }
          comments
          createdAt
          createdBy {
            id
            firstName
            lastName
          }
        }
      }

      status {
        id
        status
        statusAt
        statusBy {
          ...CrewMember
        }
      }
      ${crewMemberFragments.crewMember}
      ${crewMemberFragments.crewMemberStencil}
    }
  `,
  weldItemWithAttachmentAndRejection: gql`
  fragment WeldItemWithAttachmentAndRejection on Weld {
    id
    milestones {
      id
      name
      attachments {
        id
        url
        createdAt
        creator {
          ...CrewMemberStencil
        }
      }
      currentStatus {
        id
        codeI18n
        rejectionType {
          id
          reason
        }
        rejectionSubReasonTypes {
          id
          reason
          comments
        }
        comments
        createdAt
        createdBy {
          id
          firstName
          lastName
        }
      }
    }

    status {
      id
      status
      statusAt
      statusBy {
        ...CrewMember
      }
    }
    ${crewMemberFragments.crewMember}
    ${crewMemberFragments.crewMemberStencil}
  }
`,
  // removed rodRooms and dataValutes as its not required as of now
  mapMarkerList: gql`
    fragment MapMarkerList on MapMarkersPayload {
      commodities(types: $types, commodityComplexFilter: $commodityComplexFilter) {
        ...MapMarker
      }
      ${mapMarkerFragments.mapMarker}
    }
  `,
  commodityMapMarkers: gql`
   fragment CommodityMapMarkers on MapMarkersPayload {
     commodities(types: $types, commodityComplexFilter: $commodityComplexFilter) {
       ...MapMarker
     }
     ${mapMarkerFragments.mapMarker}
   }
 `,
};

export const GET_WELDS = gql`
  query commodities($input: CommodityFilterInput, $paging: PagingInput) {
    commodities(input: $input, paging: $paging) {
      total
      cursor
      items {
        weld {
          id
          tag
          pipeDiameter
          pipeMaterial
          locationDescription
          isPriority
          hasAssignedForeman
          currentMilestoneType {
            name
            id
          }
          milestones {
            id
            type {
              id
            }
            currentStatus {
              id
              codeI18n
              createdAt
              createdBy {
                id
                persona
                firstName
                lastName
              }
            }
            blocks {
              id
              currentStatusI18n
            }
          }

          commodityAssignees {
            assignee {
              id
              firstName
              lastName
              imageUrl
            }
            personaType
          }

          status {
            id
            status
            nameI18n
            statusAt
            statusBy {
              id
              firstName
              lastName
              persona
            }
          }
        }
      }
    }
  }
`;

export const GET_WELDS_BY_WELD_PROCEDURE = gql`
  query commodities($input: CommodityFilterInput, $paging: PagingInput) {
    commodities(input: $input, paging: $paging) {
      total
      cursor
      items {
        weld {
          id
          tag
          pipeDiameter
          pipeMaterial
          type {
            id
            name
          }
        }
      }
    }
  }
`;

export const ASSIGN_WELDING_FOREMAN = gql`
  mutation assignWeldForeman($input: AssignWeldForemanInput!) {
    assignWeldForeman(input: $input) {
      error
      commodity {
        weld {
          id
          hasAssignedForeman
          commodityAssignees {
            assignee {
              id
              firstName
              lastName
              imageUrl
              discipline
            }
            personaType
          }
          status {
            id
            status
            statusAt
            statusBy {
              id
              firstName
              lastName
              persona
            }
          }
        }
      }
    }
  }
`;

export const GET_WELDS_BY_ID = gql`
  query weldDetails($ids: [ID!]!, $paging: PagingInput) {
    commoditiesByIds(ids: $ids) {
      materialCommodity {
        id
      }
      weld {
        ...WeldItemWithRejection
        expiredRodRequestCount
        tag
        fitupRequest {
          id
          isWeldingRequired
          isFitupRequired
        }
        type {
          id
          name
        }
        applicableWeldProcedures {
          id
          procedure
        }
        weldedWithProcedure {
          id
          procedure
        }
        currentMilestoneType {
          id
          name
        }
        pipeDiameter
        pipeMaterial
        locationDescription
        isPriority
        hasAssignedForeman
        commodityAssignees {
          assignee {
            ...CrewMemberWithSupervisor
          }
          personaType
        }
        workPackage {
          id
          tag
          primaryOwners {
            crewMember {
              ...CrewMember
            }
            persona
          }
          secondaryOwners {
            crewMember {
              ...CrewMember
            }
            persona
          }
          otherOwners {
            crewMember {
              ...CrewMember
            }
            persona
          }
        }
        milestones {
          id
          type{
            id
          }
          name
          weldStatuses {
            id
          }
          currentStatus {
            id
            createdBy {
              ...CrewMember
            }
            createdAt
            codeI18n
            activity
            rejectionType {
                id
                reason
              }
            rejectionSubReasonTypes {
              id
              reason
              comments
            }
          }
          isExpired
          blocks {
              id
              currentStatusI18n
              type {
                id
                title
                description
              }
            }
        }
        location {
          elevation
          latitude
          longitude
        }
        sections {
          title
          fields {
            name
            value
            valueType
          }
        }
        isoDocument {
          documentNumber
          url
        }
        milestonesActivityHistory(paging: $paging) {
          total
          cursor
          items {
            id
            activity
            rejectionReason{
            id
            reason
            comments
            }
            rejectionSubReasons{
            id
            reason
            comments
            }
            attachments {
              id
              attachmentType
              url
              fileName
              creator {
                ...CrewMember
              }
              createdAt
            }
            blockComments {
              ...BlockCommentItem
            }
            comments
            dateTime
            milestoneName
            milestoneStatus
            crewMember {
              ...CrewMember
            }
            orderBy
            aiPlanningRequestId
            planningAiComment
          }
        }
      }
    }
    ${crewMemberFragments.crewMemberWithSupervisor}
    ${crewMemberFragments.crewMember}
    ${fragments.weldItemWithRejection}
    ${blockFragments.blockCommentItem}
  }
`;

export const GET_WELD_ATTACHMENTS = gql`
  query weldAttachments($ids: [ID!]!) {
    commoditiesByIds(ids: $ids) {
      weld {
        id
        tag
        attachments {
         ...Attachment
        }
      }
    }
    ${fragments.attachment}
  }
`;

export const ADD_COMMODITY_ATTACHMENTS = gql`
  mutation($input: AddCommodityAttachmentInput!) {
    addCommodityAttachment(input: $input) {
      commodity {
        weld {
          id
          attachments {
            ...Attachment
          }
        }
      }
      error
    }
    ${fragments.attachment}
  }
`;
export const UPDATE_COMMODITY_LOCATION_DESCRIPTION = gql`
  mutation updateCommodityLocationDescription($input: UpdateCommodityLocationDescriptionInput!) {
    updateCommodityLocationDescription(input: $input) {
      commodity {
        weld {
          id
          locationDescription
        }
      }
      error
    }
  }
`;
export const ASSIGN_UNASSIGN_PRIORITY_COMMODITY = gql`
  mutation assignUnassignPriorityCommodity($input: AssignUnassignPriorityInput!) {
    assignUnassignPriorityCommodity(input: $input) {
      commodity {
        weld {
          id
          isPriority
        }
      }
    }
  }
`;
export const ASSIGN_COMMODITY_CREW_MEMBERS = gql`
  mutation assignCommodityCrewMembers($input: AssignCommoditiesInput!) {
    assignCommodityCrewMembers(input: $input) {
      commodities {
        weld {
          id
          commodityAssignees {
            assignee {
              id
              firstName
              lastName
            }
            personaType
          }
        }
      }
      erroredCommodityAssignments {
        error
        persona
      }
    }
  }
`;
export const GET_WELD_PHOTOS = gql`
  query weldPhotos($ids: [ID!]!, $paging: PagingInput) {
    commoditiesByIds(ids: $ids) {
      weld {
        id
        milestonesActivityHistory(paging: $paging) {
          total
          cursor
          items {
            id
            milestoneName
            milestoneStatus
            attachments {
              id
              attachmentType
              url
              fileName
              fileSize
              mimeType
              creator {
                id
                firstName
                lastName
                persona
                imageUrl
              }
              createdAt
            }
          }
        }
      }
    }
  }
`;

export const LOAD_WELD_PROCEDURES = gql`
  query loadWeldProcedures($input: WeldProcedureInput, $paging: PagingInput) {
    loadWeldProcedures(input: $input, paging: $paging) {
      items {
        id
        procedure
      }
    }
  }
`;
export const GET_WELDS_BY_STATUS = gql`
  query commodities($input: CommodityFilterInput, $paging: PagingInput) {
    commodities(input: $input, paging: $paging) {
      total
      cursor
      items {
        weld {
          id
          tag
          isPriority
          commodityAssignees {
            assignee {
              id
              firstName
              lastName
              imageUrl
            }
            personaType
          }
          status {
            id
            status
            nameI18n
          }
          milestones {
            id
            isExpired
          }
          pipeDiameter
          pipeMaterial
          workPackage {
            id
          }
        }
      }
    }
  }
`;

export const GET_DASHBOARD_WELDS = gql`
  query commodities($input: CommodityFilterInput, $paging: PagingInput) {
    commodities(input: $input, paging: $paging) {
      total
      cursor
      items {
        weld {
          ...WeldItemWithRejection
          expiredRodRequestCount
          tag
          pipeDiameter
          pipeMaterial
          locationDescription
          location{
          latitude
          longitude
          }
          isPriority
          hasAssignedForeman
          fitupRequest{
            id
            isWeldingRequired
            isFitupRequired
          }
          applicableWeldProcedures {
          id
          procedure
         }
         weldedWithProcedure{
          id
          procedure
         }
         fitupRequest {
          id
         }
         currentMilestoneType {
          id
          name
          }
          commodityAssignees {
            assignee {
              id
              firstName
              lastName
              imageUrl
              badgeNumber
              supervisor {
                id
                persona
                supervisor {
                  id
                  persona
                }
              }
            }
            personaType
          }
          workPackage {
            id
            tag
            facilities {
              id
            }
            primaryOwners {
              persona
              crewMember {
                id
                persona
              }
            }
            secondaryOwners {
              persona
              crewMember {
                id
                persona
              }
            }
            otherOwners {
              persona
              crewMember {
                id
                persona
              }
            }
          }
          milestones {
            id
            name
            type {
              id
            }
            isExpired
            blocks {
              id
              currentStatusI18n

            }
          }
        }
      }
    }
    ${fragments.weldItemWithRejection}
  }
`;

export const CLAIM_WELD = gql`
  mutation claimWeld($input: ClaimWeldInput!) {
    claimWeld(input: $input) {
      error
      weld {
        ...WeldItemWithAttachment
      }
    }
  }
  ${fragments.weldItemWithAttachment}
`;

export const REJECT_WELD = gql`
  mutation rejectWeld($input: RejectWeldInput!) {
    rejectWeld(input: $input) {
      error
      weld {
        ...WeldItemWithAttachmentAndRejection
      }
    }
    ${fragments.weldItemWithAttachmentAndRejection}
  }
`;

export const RECLAIM_WELD = gql`
  mutation reclaimWeld($input: ReclaimWeldInput!) {
    reclaimWeld(input: $input) {
      error
      weld {
        ...WeldItemWithAttachment
      }
    }
    ${fragments.weldItemWithAttachment}
  }
`;

export const UNCLAIM_WELD = gql`
  mutation unclaimWeld($input: MilestoneStatusInput!) {
    unclaimWeld(input: $input) {
      error
      weld {
        ...WeldItem
      }
    }
    ${fragments.weldItem}
  }
`;

export const APPROVE_WELD = gql`
  mutation approveWeld($input: MilestoneStatusInput!) {
    approveWeld(input: $input) {
      error
      weld {
        ...WeldItem
      }
    }
    ${fragments.weldItem}
  }
`;

export const UNAPPROVE_WELD = gql`
  mutation unapproveWeld($input: MilestoneStatusInput!) {
    unapproveWeld(input: $input) {
      error
      weld {
        ...WeldItem
      }
    }
    ${fragments.weldItem}
  }
`;

export const APPROVE_FITUP = gql`
  mutation approveFitup($input: MilestoneStatusInput!) {
    approveFitup(input: $input) {
      error
      weld {
        ...WeldItem
      }
    }
    ${fragments.weldItem}
  }
`;

export const CANCEL_FITUP_REQUEST = gql`
  mutation cancelFitupRequest($input: MilestoneStatusInput!) {
    cancelFitupRequest(input: $input) {
      error
      weld {
        ...WeldItem
        commodityAssignees {
          assignee {
            id
          }
        }
        fitupRequest  {
          id
        }
      }
    }
    ${fragments.weldItem}
  }
`;

export const REJECT_FITUP = gql`
  mutation rejectFitup($input: RejectFitupInput!) {
    rejectFitup(input: $input) {
      error
      weld {
        ...WeldItemWithAttachmentAndRejection
      }
    }
    ${fragments.weldItemWithAttachmentAndRejection}
  }
`;

export const GET_COMMODITY_STATS = gql`
  query commodityStats($input: CommodityFilterInput!) {
    commodityStats(input: $input) {
      commodityStats {
        id
        numCrews
        numWelders
        statusStats {
          stats
          status
        }
      }
    }
  }
`;
export const GET_COMMODITY_STATS_PRIORITY = gql`
  query commodityStatsPriority($input: CommodityFilterInput!) {
    commodityStats(input: $input) {
      commodityStats {
        id
        numPriorityWelds
      }
    }
  }
`;
export const GET_COMMODITY_STATS_CLAIMED_UNASSIGNED_FWE = gql`
  query commodityStatsClaimedAndUnassignedFWE($input: CommodityFilterInput!) {
    commodityStats(input: $input) {
      commodityStats {
        id
        numUnassigned
      }
    }
  }
`;
export const GET_COMMODITY_STATS_REJECTED_FWE = gql`
  query commodityStatsRejectedFWE($input: CommodityFilterInput!) {
    commodityStats(input: $input) {
      commodityStats {
        id
        statusStats {
          stats
          status
        }
      }
    }
  }
`;
export const GET_COMMODITY_WEEKLY_STATS = gql`
  query commodityWeeklyStats($input: CommodityFilterInput!) {
    commodityStats(input: $input) {
      commodityStats {
        id
        statusStats {
          stats
          status
        }
      }
    }
  }
`;
export const GET_COMMODITY_DASHBOARD_TAB_STATS = gql`
  query commodityDashboardTabStats($input: CommodityFilterInput!) {
    commodityStats(input: $input) {
      commodityStats {
        id
        statusStats {
          stats
          status
        }
      }
    }
  }
`;
export const GET_COMMODITY_BLOCKS_STATS_FWE = gql`
  query commodityBlocksStatsFWE($input: CommodityFilterInput!) {
    commodityStats(input: $input) {
      commodityStats {
        id
        statusStats {
          stats
          status
        }
      }
    }
  }
`;
export const GET_COMMODITY_ALL_PRIORITY_STATS = gql`
  query commodityStatsAllPriorityStats($input: CommodityFilterInput!) {
    commodityStats(input: $input) {
      commodityStats {
        id
        numPriorityWelds
      }
    }
  }
`;
export const GET_ASSIGN_TO_ME_STATS_FWE = gql`
  query assignToMeStatsFWE($input: CommodityFilterInput!) {
    commodityStats(input: $input) {
      commodityStats {
        id
        numAssignedToMe
      }
    }
  }
`;

export const GET_COMMODITY_FITUP_STATS = gql`
  query commodityFitupStats($input: CommodityFilterInput!) {
    commodityStats(input: $input) {
      commodityStats {
        id
        statusStats {
          stats
          status
        }
      }
    }
  }
`;

export const GET_WELD_LIST_BY_IDS = gql`
  query weldList($ids: [ID!]!) {
    commoditiesByIds(ids: $ids) {
      weld {
        id
        tag
        isFitupRequired
        type {
          id
          name
          projectMilestoneTypes{
            id
            name
           }
        }
        itrRequired
        status {
        id
        status
      }
        fitupRequest {
          id
          installationCrewMember {
            ...CrewMember
          }
        }
        commodityAssignees {
          assignee {
            ...CrewMemberWithSupervisor
          }
          personaType
        }
        applicableWeldProcedures {
          id
          procedure
        }
        currentMilestoneType {
          id
        }
        milestones {
          id
          type{
            id
          }
          name
          currentStatus {
            id
            codeI18n
            createdAt
          }
          blocks {
            id
            currentStatusI18n
          }
        }
      }
    }
    ${crewMemberFragments.crewMember}
    ${crewMemberFragments.crewMemberWithSupervisor}
  }
`;

export const GET_WELDS_DETAIL_BY_IDS = gql`
  query weldDetailsByIds($ids: [ID!]!, $paging: PagingInput) {
    commoditiesByIds(ids: $ids) {
      materialCommodity {
        id
      }
      weld {
        id
        tag
        isFitupRequired
        workPackage {
          id
          tag
        }
        currentMilestoneType {
          name
          id
        }
        type {
          id
          name
          projectMilestoneTypes{
           id
           name
          }
        }
        status{
          id
          status
        }
        applicableWeldProcedures {
          id
          procedure
        }
        fitupRequest{
          ...fitupRequest
          }
        weldedWithProcedure {
          id
          procedure
        }
        location {
          latitude
          longitude
        }
        locationDescription
        hasAssignedForeman
        itrRequired
        commodityAssignees {
          assignee {
            ...CrewMemberWithSupervisor
          }
          personaType
        }

        milestones {
          id
          name
          hours
          timesRejected
          plannedStartDate
          plannedEndDate
          quantity {
              ...Quantity
            }
          attachments {
            id
            url
            createdAt
            creator {
              id
              firstName
              lastName
              persona
              imageUrl
              }
          }
          type {
            id
          }
          blocks {
            id
            currentStatusI18n
          }
          currentStatus {
            id
            createdBy {
              ...CrewMember
            }
            createdAt
            codeI18n
            comments
            activity
            rejectionType {
                id
                reason
              }
            rejectionSubReasonTypes {
              id
              reason
              comments
           }
         }
        }
        sections {
          title
          fields {
            name
            value
            valueType
          }
        }
        milestonesActivityHistory(paging: $paging) {
          total
          cursor
          items {
            id
            activity
            attachments {
              id
              attachmentType
              url
              fileName
              creator {
                ...CrewMember
              }
              createdAt
            }
            blockComments {
              ...BlockCommentItem
            }
            comments
            dateTime
            milestoneName
            milestoneStatus
            crewMember {
              ...CrewMember
            }
            orderBy
            aiPlanningRequestId
            planningAiComment
          }
        }
    }
    }
    ${workPackageFragments.quantity}
    ${crewMemberFragments.crewMemberWithSupervisor}
    ${crewMemberFragments.crewMember}
    ${fragments.fitupRequest}
    ${blockFragments.blockCommentItem}
  }
`;

export const CREATE_FITUP_REQUEST = gql`
  mutation createFitupRequests($input: CreateFitupRequestsInput!) {
    createFitupRequests(input: $input) {
      erroredWelds {
        errorCode
        weld {
          id
          tag
        }
      }
      welds {
        id
        itrRequired
        milestones {
          id
          type{
            id
          }
          name
          currentStatus {
            id
            codeI18n
            createdAt
                createdBy {
                id
                persona
                firstName
                lastName
              }
          }
          blocks {
            id
            currentStatusI18n
          }
        }
        fitupRequest{
         ...fitupRequest
         }
      }
    }
    ${crewMemberFragments.crewMember}
    ${fragments.fitupRequest}
  }
`;

export const BULK_ROD_REQUEST_PROGRESS = gql`
  mutation progressRodRequestWelds($input: BulkRodRequestProgressInput!) {
    progressRodRequestWelds(input: $input) {
      rodRequestProgressPayload {
        error {
          errorCode
          rodRequest {
            id
            requestNumber
          }
        }
        rodRequest {
          id
          currentStatusI18n
          stencils {
            welds {
              id
              status
              weld {
                id
                tag
                pipeDiameter
                pipeMaterial
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_COMMODITY_STATS_SUMMARY = gql`
  query commodityStatsSummary($input: CommodityFilterInput!) {
    commodityStatsSummary(input: $input) {
      errors
      numCommodities
      statusStats {
        stats
        status
      }
    }
  }
`;

export const CLAIM_AND_APPROVE_WELD = gql`
  mutation claimApproveWeld($input: ClaimApproveWeldInput!) {
    claimApproveWeld(input: $input) {
      error
      weld {
        id
        status {
          id
          status
        }
      }
    }
  }
`;

export const REASSIGN_FITUP_REQUEST = gql`
  mutation reassignFitupRequest($input: ReassignFitupRequestInput!) {
    reassignFitupRequest(input: $input) {
      errorCode
      weld {
        id
        itrRequired
        fitupRequest{
         ...fitupRequest
         }
      }
    }
    ${fragments.fitupRequest}
  }
`;

export const UNAPPROVE_FITUP_MILESTONE = gql`
  mutation unapproveFitup($input: MilestoneStatusInput!) {
    unapproveFitup(input: $input) {
      error
      weld {
        ...WeldItem
      }
    }
    ${fragments.weldItem}
  }
`;
export const FWE_MAP_MARKERS_QUERY = gql`
  query fweMapMarkers($types: [MarkerType!]!, $commodityComplexFilter: CommodityComplexFilterInput) {
    mapMarkers(types: $types, commodityComplexFilter: $commodityComplexFilter) {
      ...MapMarkerList
    }
    ${fragments.mapMarkerList}
  }
`;
export const FWE_FITUP_MAP_MARKERS_QUERY = gql`
  query fweFitupMapMarkers($types: [MarkerType!]!, $commodityComplexFilter: CommodityComplexFilterInput) {
    mapMarkers(types: $types, commodityComplexFilter: $commodityComplexFilter) {
      ...CommodityMapMarkers
    }
    ${fragments.commodityMapMarkers}
  }
`;
export const FWE_APPROVED_MAP_MARKERS_QUERY = gql`
  query fweApprovedMapMarkers($types: [MarkerType!]!, $commodityComplexFilter: CommodityComplexFilterInput) {
    mapMarkers(types: $types, commodityComplexFilter: $commodityComplexFilter) {
      ...CommodityMapMarkers
    }
    ${fragments.commodityMapMarkers}
  }
`;

export const MAP_MARKERS_QUERY = gql`
  query mapMarkers($types: [MarkerType!]!, $commodityComplexFilter: CommodityComplexFilterInput) {
    mapMarkers(types: $types, commodityComplexFilter: $commodityComplexFilter) {
      ...MapMarkerList
    }
    ${fragments.mapMarkerList}
  }
`;

export const ADD_WELD_ATTACHMENT = gql`
  mutation addWeldAttachment($input: AddWeldAttachmentInput!) {
    addWeldAttachment(input: $input) {
      weld {
        id
      }
      error
    }
  }
`;

export const SUBSCRIPTION_WELD_PROCEDURE_UPDATED = gql`
  subscription onWeldProcedureUpdated {
    weldProcedureUpdated {
      weldProcedureIds
    }
  }
`;

export const DELETE_COMMODITY_ATTACHMENT = gql`
  mutation deleteCommodityAttachment($input: DeleteCommodityAttachmentInput!) {
    deleteCommodityAttachment(input: $input) {
      commodity {
        weld {
          id
          attachments {
            id
          }
        }
      }
      error
    }
  }
`;

export const DELETE_WELD_ATTACHMENT = gql`
  mutation deleteWeldAttachment($input: DeleteWeldAttachmentInput!) {
    deleteWeldAttachment(input: $input) {
      weld {
        id
        attachments {
          id
        }
      }
      error
    }
  }
`;

export const GET_COMMODITY_PRIORITY_COUNT = gql`
  query commodityPriorityCount($input: CommodityFilterInput, $paging: PagingInput) {
    commodities(input: $input, paging: $paging) {
      priorityCommodityCount(input: $input)
    }
  }
`;

/**
 * This query is used to handle a special case where Send back to piping action needs to be
 * taken on a weld whose fitup milestone is not available
 */
export const REJECT_FITUP_REQUEST = gql`
  mutation rejectFitupRequest($input: RejectFitupRequestInput!) {
    rejectFitupRequest(input: $input) {
      error
      weld {
        ...WeldItemWithAttachmentAndRejection
      }
    }
    ${fragments.weldItemWithAttachmentAndRejection}
  }
`;
